import { token } from '@/infrastructure/container';
import { EventBus, type EventSubscription } from '@/infrastructure/EventBus';
import { type ILogger } from './Logger';
import { type SimulationPhase, type SourceKind } from '@/domain/beforered';

export type AppBusEvents = {
  TOAST_REQUESTED: { kind: 'success' | 'error' | 'info'; message: string };
  THEME_CHANGED: { theme: 'light' | 'dark' };
  KEY_DOWN: {
    key: string;
    ctrl: boolean;
    meta: boolean;
    shift: boolean;
    alt: boolean;
    preventDefault: () => void;
  };
  IMPORT_COMPLETED: {
    source: SourceKind;
    filename: string;
    rowCount: number;
  };
  MAPPING_CONFIRMED: {
    source: SourceKind;
    mapping: Record<string, string | null>;
  };
  SIMULATION_PHASE: {
    phase: SimulationPhase;
    skusProcessed: number;
    skusTotal: number;
  };
  SIMULATION_COMPLETED: {
    recommendationCount: number;
    durationMs: number;
  };
  EXPORT_TRIGGERED: {
    filename: string;
    rowCount: number;
  };
  VALIDATION_WARNING: {
    source: SourceKind;
    message: string;
    count: number;
  };
};

export interface IAppBus {
  toast(kind: AppBusEvents['TOAST_REQUESTED']['kind'], message: string): void;
  themeChanged(theme: AppBusEvents['THEME_CHANGED']['theme']): void;
  keyDown(data: AppBusEvents['KEY_DOWN']): void;
  importCompleted(data: AppBusEvents['IMPORT_COMPLETED']): Promise<void>;
  mappingConfirmed(data: AppBusEvents['MAPPING_CONFIRMED']): void;
  simulationPhase(data: AppBusEvents['SIMULATION_PHASE']): void;
  simulationCompleted(data: AppBusEvents['SIMULATION_COMPLETED']): Promise<void>;
  exportTriggered(data: AppBusEvents['EXPORT_TRIGGERED']): Promise<void>;
  validationWarning(data: AppBusEvents['VALIDATION_WARNING']): void;
  subscribe<K extends keyof AppBusEvents>(
    event: K,
    handler: (data: AppBusEvents[K]) => void | Promise<void>,
  ): EventSubscription;
}

export const TOKEN_IAppBus = token<IAppBus>('IAppBus');

export class AppBus implements IAppBus {
  private bus: EventBus<AppBusEvents>;

  constructor(logger: ILogger) {
    this.bus = new EventBus<AppBusEvents>(logger);
  }

  toast(kind: AppBusEvents['TOAST_REQUESTED']['kind'], message: string): void {
    this.bus.publish('TOAST_REQUESTED', { kind, message });
  }

  themeChanged(theme: AppBusEvents['THEME_CHANGED']['theme']): void {
    this.bus.publish('THEME_CHANGED', { theme });
  }

  keyDown(data: AppBusEvents['KEY_DOWN']): void {
    this.bus.publish('KEY_DOWN', data);
  }

  importCompleted(data: AppBusEvents['IMPORT_COMPLETED']): Promise<void> {
    return this.bus.publishAndWait('IMPORT_COMPLETED', data);
  }

  mappingConfirmed(data: AppBusEvents['MAPPING_CONFIRMED']): void {
    this.bus.publish('MAPPING_CONFIRMED', data);
  }

  simulationPhase(data: AppBusEvents['SIMULATION_PHASE']): void {
    this.bus.publish('SIMULATION_PHASE', data);
  }

  simulationCompleted(data: AppBusEvents['SIMULATION_COMPLETED']): Promise<void> {
    return this.bus.publishAndWait('SIMULATION_COMPLETED', data);
  }

  exportTriggered(data: AppBusEvents['EXPORT_TRIGGERED']): Promise<void> {
    return this.bus.publishAndWait('EXPORT_TRIGGERED', data);
  }

  validationWarning(data: AppBusEvents['VALIDATION_WARNING']): void {
    this.bus.publish('VALIDATION_WARNING', data);
  }

  subscribe<K extends keyof AppBusEvents>(
    event: K,
    handler: (data: AppBusEvents[K]) => void | Promise<void>,
  ): EventSubscription {
    return this.bus.subscribe(event, handler);
  }

  handlerCount<K extends keyof AppBusEvents>(event: K): number {
    return this.bus.handlerCount(event);
  }
}
